import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AdminDashboardApi, ApplicationHealthApi, OnlineTenantUsersStatsDto } from '@buspro/web/apis';
import { WEB_CONFIG, WebAppEnvironment } from '@buspro/web/core';
import { Observable, lastValueFrom } from 'rxjs';

export interface PlatformOverviewStats {
  tenants: {
    total: number;
    active: number;
    provisioning: number;
    suspended: number;
    terminated: number;
  };
  buses: {
    total: number;
    active: number;
    maintenance: number;
    offline: number;
  };
  activeTrips: number;
  completedTrips: {
    today: number;
    yesterday: number;
    growth: number;
  };
  todayRevenue: number;
  yesterdayRevenue: number;
  revenueGrowth: number;
  systemHealth: {
    status: 'healthy' | 'degraded' | 'down';
    uptime: number;
    apiStatus: string;
    dbStatus: string;
  };
  averageAuditDuration: number;
  inventory?: InventoryStats;
}

export interface InventoryStats {
  mdvrDevices: {
    total: number;
    available: number;
    allocated: number;
    installed: number;
    faulty: number;
  };
  simCards: {
    total: number;
    available: number;
    allocated: number;
    installed: number;
    faulty: number;
  };
  posDevices: {
    total: number;
    available: number;
    allocated: number;
    installed: number;
    faulty: number;
  };
  wialonDevices: {
    total: number;
    available: number;
    allocated: number;
    installed: number;
    faulty: number;
  };
  lowStockAlerts: number;
}

export interface SystemHealthResponse {
  status?: string;
  info?: {
    [key: string]: {
      status: string;
      [key: string]: any;
    };
  } | null;
  error?: {
    [key: string]: {
      status: string;
      [key: string]: any;
    };
  } | null;
  details?: {
    [key: string]: {
      status: string;
      [key: string]: any;
    };
  };
}

@Injectable({
  providedIn: 'root',
})
export class AdminDashboardApiService {
  private http = inject(HttpClient);
  private environment = inject<WebAppEnvironment>(WEB_CONFIG);
  private healthApi = inject(ApplicationHealthApi);
  private dashboardApi = inject(AdminDashboardApi);

  private get apiUrl(): string {
    return `${this.environment.api}/admin/dashboard`;
  }

  private get inventoryApiUrl(): string {
    return `${this.environment.api}/inventory`;
  }

  /**
   * Get platform overview statistics
   * Aggregates data across ALL tenants in the platform
   */
  getPlatformOverviewStats(): Observable<PlatformOverviewStats> {
    return this.http.get<PlatformOverviewStats>(`${this.apiUrl}/platform-overview`);
  }

  /**
   * Get inventory statistics for admin dashboard
   * Returns aggregated counts for MDVR devices, SIM cards, and Android POS devices
   */
  getInventoryStats(): Observable<InventoryStats> {
    return this.http.get<InventoryStats>(`${this.inventoryApiUrl}/stats`);
  }

  /**
   * Get comprehensive system health status
   * Returns health status of database, Redis, Firebase, Pusher, MDVR, memory, and disk
   */
  getSystemHealth(): Observable<SystemHealthResponse> {
    return this.healthApi.healthControllerCheck();
  }

  /**
   * Get online tenant users statistics from Pusher presence channel
   * Returns real-time count of online users per tenant
   */
  getOnlineTenantUsersStats(): Observable<OnlineTenantUsersStatsDto> {
    return this.dashboardApi.adminDashboardControllerGetOnlineTenantUsersStats();
  }
}
